import React, {Fragment, useEffect, useState} from "react";

// @mui/icons-material
// core components
import Button from "../../components/CustomButtons/Button.js";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import {ROLE_SCHOOL_ADMIN_ID} from "../../util/roles";
import {useStudentSchoolGetRequest, useTeacherInviteEmployerRequest,} from "../../request/api";

const MIN_VERIFIED_USERS = 100;

export function InviteEmployerDialog({ open, onClose, org, auth, trackVisit }) {
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [hasTrackedVisit, setHasTrackedVisit] = useState(false);
  const [hasAttemptedInvite, setHasAttemptedInvite] = useState(false);

  const isSchoolAdmin = auth.user?.role === ROLE_SCHOOL_ADMIN_ID;

  const {
    data: studentSchoolData,
    isLoading: isLoadingStudentSchool,
    error: studentSchoolError,
  } = useStudentSchoolGetRequest(
    {
      shouldFetch: open && isSchoolAdmin,
      revalidateOnFocus: false,
    },
    { withCredentials: true }
  );

  const { trigger: sendInvite, isMutating: isInviting } = useTeacherInviteEmployerRequest({ employerId: org?.id }, undefined, {
    withCredentials: true,
  });

  useEffect(() => {
    if (!open) {
      setInviteSuccess(false);
      setInviteError(null);
      setHasTrackedVisit(false);
      setHasAttemptedInvite(false);
    }
  }, [open]);

  const verifiedAndRegisteredUserCount = studentSchoolData?.school?.verifiedRegisteredUsersCount ?? 0;
  const hasRequiredUsers = verifiedAndRegisteredUserCount >= MIN_VERIFIED_USERS;
  const canAttemptInvite = hasRequiredUsers && !isLoadingStudentSchool && !studentSchoolError;

  useEffect(() => {
    if (open && hasRequiredUsers && !hasTrackedVisit && typeof trackVisit === "function") {
      (async () => {
        try {
          await trackVisit();
        }
        catch (error) {
          console.error(error);
        }
        finally {
          setHasTrackedVisit(true);
        }
      })();
    }
  }, [open, hasRequiredUsers, hasTrackedVisit, trackVisit]);

  useEffect(() => {
    if (open && canAttemptInvite && !hasAttemptedInvite && !inviteSuccess) {
      (async () => {
        try {
          await sendInvite();
          setInviteSuccess(true);
          window.gtag && window.gtag("event", "employer_invite_interest", {
            org_id: org?.id,
          });
        }
        catch (error) {
          console.error(error);
          const responseMessage = error?.response?.data?.message;
          setInviteError(responseMessage || "We couldn't register your interest just now. Please try again later.");
        }
        finally {
          setHasAttemptedInvite(true);
        }
      })();
    }
  }, [open, canAttemptInvite, hasAttemptedInvite, inviteSuccess, sendInvite, org?.id]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="invite-employer-dialog-title">
      <DialogTitle id="invite-employer-dialog-title">Invite employer</DialogTitle>
      <DialogContent dividers>
        {isLoadingStudentSchool ? (
          <Typography>Checking your school's eligibility...</Typography>
        ) : studentSchoolError ? (
          <Typography color="error">We couldn't check your school's verified user count. Please try again later.</Typography>
        ) : !hasRequiredUsers ? (
          <>
            <Typography gutterBottom>
              You need at least {MIN_VERIFIED_USERS} verified and registered users before inviting an employer to visit your school.
            </Typography>
            <Typography>
              {verifiedAndRegisteredUserCount > 0
                ? `You currently have ${verifiedAndRegisteredUserCount}. Please validate your pending users to continue.`
                : "Please validate your pending users to continue."}
            </Typography>
          </>
        ) : isInviting && !inviteSuccess ? (
          <Typography>Registering your interest...</Typography>
        ) : inviteError ? (
          <Typography color="error">{inviteError}</Typography>
        ) : inviteSuccess ? (
          <Typography>
            Thanks! Your interest has been registered. We'll let {org.name} know you're interested in welcoming them to your school.
          </Typography>
        ) : (
          <Typography>Preparing your invitation...</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} className="button">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
